export class ApiClient {
    baseUrl;
    token = null;
    constructor(config) {
        this.baseUrl = config.baseUrl;
        this.token = config.token ?? null;
    }
    setToken(token) {
        this.token = token;
    }
    clearToken() {
        this.token = null;
    }
    async request(method, path, body) {
        const headers = {
            'Content-Type': 'application/json',
        };
        // Support both cookie-based auth (Better Auth) and token-based auth (MCP)
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        try {
            const response = await fetch(`${this.baseUrl}${path}`, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
                credentials: 'include', // Include cookies for Better Auth session
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                return { error: errorData.error || `HTTP ${response.status}` };
            }
            const data = await response.json();
            return { data };
        }
        catch (error) {
            return { error: error instanceof Error ? error.message : 'Unknown error' };
        }
    }
    // Auth endpoints
    async login(email, password) {
        return this.request('POST', '/api/auth/login', { email, password });
    }
    async register(email, password) {
        return this.request('POST', '/api/auth/register', { email, password });
    }
    async logout() {
        return this.request('POST', '/api/auth/logout');
    }
    // Chat endpoints
    async sendMessage(message, conversationId, history) {
        return this.request('POST', '/api/chat/message', { message, conversationId, history });
    }
    /**
     * Send a message with streaming status updates.
     * Returns an AbortController that can be used to cancel the stream.
     */
    sendMessageStream(message, callbacks, conversationId, options) {
        const controller = new AbortController();
        const headers = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        (async () => {
            try {
                const response = await fetch(`${this.baseUrl}/api/chat-v2/message/stream`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        message,
                        conversationId,
                        mode: options?.mode,
                        model: options?.model,
                        attachedWorkflow: options?.attachedWorkflow,
                    }),
                    signal: controller.signal,
                    credentials: 'include', // Include cookies for Better Auth session
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    callbacks.onError?.(errorData.error || `HTTP ${response.status}`);
                    return;
                }
                const reader = response.body?.getReader();
                if (!reader) {
                    callbacks.onError?.('No response body');
                    return;
                }
                const decoder = new TextDecoder();
                let buffer = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done)
                        break;
                    buffer += decoder.decode(value, { stream: true });
                    // Process complete SSE messages
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || ''; // Keep incomplete message in buffer
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const event = JSON.parse(line.slice(6));
                                switch (event.type) {
                                    case 'status':
                                        callbacks.onStatus?.(event.message);
                                        break;
                                    case 'thinking':
                                        callbacks.onThinking?.(event.content);
                                        break;
                                    case 'tool_start':
                                        callbacks.onToolStart?.(event.tool, event.input, event.iteration);
                                        break;
                                    case 'tool_end':
                                        callbacks.onToolEnd?.(event.tool, event.result, event.iteration);
                                        break;
                                    case 'plan_created':
                                        callbacks.onPlanCreated?.(event.plan);
                                        break;
                                    case 'questions_asked':
                                        callbacks.onQuestionsAsked?.(event.questions);
                                        break;
                                    case 'build_started':
                                        callbacks.onBuildStarted?.();
                                        break;
                                    case 'build_progress':
                                        callbacks.onBuildProgress?.(event.step);
                                        break;
                                    case 'complete':
                                        callbacks.onComplete?.(event.data);
                                        break;
                                    case 'error':
                                        callbacks.onError?.(event.message);
                                        break;
                                    case 'insufficient_credits':
                                        callbacks.onInsufficientCredits?.(event.credits);
                                        break;
                                }
                            }
                            catch {
                                // Ignore parse errors
                            }
                        }
                    }
                }
            }
            catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return; // Cancelled, don't call error callback
                }
                callbacks.onError?.(error instanceof Error ? error.message : 'Unknown error');
            }
        })();
        return controller;
    }
    /**
     * Build a workflow from an approved plan.
     * Returns an AbortController that can be used to cancel the stream.
     */
    buildFromPlan(conversationId, plan, callbacks, options) {
        const controller = new AbortController();
        const headers = {
            'Content-Type': 'application/json',
        };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        (async () => {
            try {
                const response = await fetch(`${this.baseUrl}/api/chat-v2/build`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ conversationId, plan, model: options?.model }),
                    signal: controller.signal,
                    credentials: 'include', // Include cookies for Better Auth session
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    callbacks.onError?.(errorData.error || `HTTP ${response.status}`);
                    return;
                }
                const reader = response.body?.getReader();
                if (!reader) {
                    callbacks.onError?.('No response body');
                    return;
                }
                const decoder = new TextDecoder();
                let buffer = '';
                while (true) {
                    const { done, value } = await reader.read();
                    if (done)
                        break;
                    buffer += decoder.decode(value, { stream: true });
                    // Process complete SSE messages
                    const lines = buffer.split('\n\n');
                    buffer = lines.pop() || ''; // Keep incomplete message in buffer
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const event = JSON.parse(line.slice(6));
                                switch (event.type) {
                                    case 'status':
                                        callbacks.onStatus?.(event.message);
                                        break;
                                    case 'build_started':
                                        callbacks.onBuildStarted?.();
                                        break;
                                    case 'build_progress':
                                        callbacks.onBuildProgress?.(event.step);
                                        break;
                                    case 'complete':
                                        callbacks.onComplete?.(event.data);
                                        break;
                                    case 'error':
                                        callbacks.onError?.(event.message);
                                        break;
                                    case 'insufficient_credits':
                                        callbacks.onInsufficientCredits?.(event.credits);
                                        break;
                                }
                            }
                            catch {
                                // Ignore parse errors
                            }
                        }
                    }
                }
            }
            catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return; // Cancelled, don't call error callback
                }
                callbacks.onError?.(error instanceof Error ? error.message : 'Unknown error');
            }
        })();
        return controller;
    }
    // Conversation endpoints
    async getConversations() {
        return this.request('GET', '/api/chat/conversations');
    }
    async getConversation(id) {
        return this.request('GET', `/api/chat/conversations/${id}`);
    }
    async getConversationFull(id) {
        return this.request('GET', `/api/chat/conversations/${id}/full`);
    }
    async updateConversationTitle(id, title) {
        return this.request('PATCH', `/api/chat/conversations/${id}`, { title });
    }
    async deleteConversation(id) {
        return this.request('DELETE', `/api/chat/conversations/${id}`);
    }
    async getConversationDebugLogs(id) {
        return this.request('GET', `/api/chat/conversations/${id}/debug-logs`);
    }
    // Workflow endpoints
    async getWorkflows() {
        return this.request('GET', '/api/workflows');
    }
    async getWorkflow(id) {
        return this.request('GET', `/api/workflows/${id}`);
    }
    async saveWorkflow(workflow) {
        return this.request('POST', '/api/workflows', { workflow });
    }
    async updateWorkflow(id, workflow) {
        return this.request('PUT', `/api/workflows/${id}`, { workflow });
    }
    async deleteWorkflow(id) {
        return this.request('DELETE', `/api/workflows/${id}`);
    }
    // n8n endpoints
    async testN8nConnection(url, apiKey) {
        return this.request('POST', '/api/n8n/test-connection', { url, apiKey });
    }
    async deployWorkflow(workflow, workflowId) {
        return this.request('POST', '/api/n8n/deploy', { workflow, workflowId });
    }
    async deleteFromN8n(workflowId) {
        return this.request('DELETE', `/api/n8n/workflows/${workflowId}`);
    }
    async listN8nWorkflows(instanceId) {
        const query = instanceId ? `?instanceId=${encodeURIComponent(instanceId)}` : '';
        return this.request('GET', `/api/n8n/workflows${query}`);
    }
    async importN8nWorkflows(workflowIds, instanceId) {
        return this.request('POST', '/api/n8n/import', { workflowIds, ...(instanceId && { instanceId }) });
    }
    // n8n credential endpoints
    async listN8nCredentials() {
        return this.request('GET', '/api/n8n/credentials');
    }
    async getCredentialSchema(typeName) {
        return this.request('GET', `/api/n8n/credentials/schema/${typeName}`);
    }
    async createN8nCredential(name, type, data) {
        return this.request('POST', '/api/n8n/credentials', { name, type, data });
    }
    async deleteN8nCredential(id) {
        return this.request('DELETE', `/api/n8n/credentials/${id}`);
    }
    // Get active n8n instance (for Settings page display)
    async getActiveInstance() {
        return this.request('GET', '/api/mcp/instances/active');
    }
    // MCP API Keys endpoints
    async getApiKeys() {
        return this.request('GET', '/api/mcp/api-keys');
    }
    async createApiKey(name) {
        return this.request('POST', '/api/mcp/api-keys', { name });
    }
    async deleteApiKey(id) {
        return this.request('DELETE', `/api/mcp/api-keys/${id}`);
    }
    // MCP n8n Instances endpoints
    async getInstances() {
        return this.request('GET', '/api/mcp/instances');
    }
    async createInstance(name, url, apiKey) {
        return this.request('POST', '/api/mcp/instances', { name, url, apiKey });
    }
    async updateInstance(id, data) {
        return this.request('PUT', `/api/mcp/instances/${id}`, data);
    }
    async deleteInstance(id) {
        return this.request('DELETE', `/api/mcp/instances/${id}`);
    }
    async activateInstance(id) {
        return this.request('POST', `/api/mcp/instances/${id}/activate`);
    }
    async testInstance(id) {
        return this.request('POST', `/api/mcp/instances/${id}/test`);
    }
    // Credits endpoints
    async getCredits() {
        return this.request('GET', '/api/user/credits');
    }
    // Account data endpoints (danger zone)
    async getDataCounts() {
        return this.request('GET', '/api/account/data-counts');
    }
    async deleteAllConversations(confirmPhrase) {
        return this.request('DELETE', '/api/account/conversations', { confirmPhrase });
    }
    async deleteAllWorkflows(confirmPhrase) {
        return this.request('DELETE', '/api/account/workflows', { confirmPhrase });
    }
    // Bug report
    async submitBugReport(data) {
        return this.request('POST', '/api/bug-reports', data);
    }
}
// Factory function for creating client
export function createApiClient(baseUrl) {
    return new ApiClient({ baseUrl });
}
//# sourceMappingURL=index.js.map